// fetchMessageDetail.js
'use strict';
import 'isomorphic-fetch';

import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import getMeData from '../selector/getMeData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getMessageData from '../selector/getMessageData.js';
import savePosts from '../action/savePosts.js';

import {
  MERGE_USER,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch message detail
 * @kind action
 * @param {string} {id} - message id.
 * @param {object} {[httpProxyHeaders = {}]} - http proxy headers for SSR.
 * @return {Promise} Action promise.
 */
const fetchMessageDetail =
  ({ id, httpProxyHeaders = {} }) =>
  async (dispatch, getState) => {
    const state = getState();

    const selectPath = ['messages', id, 'detail'];

    const isFetching = getNetworkingData(state, selectPath, 'isFetching');
    if (isFetching) {
      return { type: '' };
    }

    const lastEtag = getNetworkingData(state, selectPath, 'etag');

    const url = getResourceUrl({ endpoint: `/messages/${id}` });
    const fetchOptions = {
      headers: {
        ...getHeaders(),
        ...httpProxyHeaders,
      },
    };

    const token = getMeData(state, 'token');
    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (200 !== response.status) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const etag = response.headers.get('etag');
      if (etag && etag === lastEtag) {
        return dispatch({
          type: SET_NETWORKING_SUCCESS,
          payload: { selectPath, etag },
        });
      }

      const { replyOf, root: rootMessage, ...message } = await response.json();

      let messages = [];
      const mainMessage = { ...message };

      if (replyOf) {
        messages.push(replyOf);
        mainMessage.replyOfId = replyOf.id;
      }

      if (rootMessage) {
        messages.push(rootMessage);
        mainMessage.rootId = rootMessage.id;
      }

      if (undefined != mainMessage.replyPrice) {
        dispatch({
          type: MERGE_USER,
          payload: {
            user: {
              id: mainMessage.sender,
              replyPrice: mainMessage.replyPrice,
            },
          },
        });
      }

      delete mainMessage.replyPrice;

      messages.push(mainMessage);

      dispatch(
        savePosts({
          posts: messages.map(message => {
            // Auto voice and auto story message's postedAt is a setting time not a post time
            if (
              message?.mediaType === 'audio/mp4' ||
              message?.mediaType === 'video/mp4'
            ) {
              const existPostedAt = getMessageData(getState(), id, 'postedAt');
              const existPostedAtUnix = getMessageData(
                getState(),
                id,
                'postedAtUnix'
              );
              if (existPostedAt) delete message.postedAt;
              if (existPostedAtUnix) delete message.postedAtUnix;
            }
            return message;
          }),
        })
      );
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath, etag },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchMessageDetail;
